import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { hideRouteAlert, selectRouteAlert } from "@/reducers/overlaysSlice"
import { useCallback, useEffect, useRef, useState } from "react"
import MapView, { MapViewHandle } from "@/components/maps/MapView"
import AdHocCommands from "@/api/adHocCommands"
import RoutePolylinesParser from "@/lib/routePolylinesPareser"
import { RouteInfo } from "./RouteView"
import { Route2g, RouteStop } from "@/reducers/routes2gSlice"
import { Source } from "./mapLibre/Source"
import { Layer, LayerProps } from "./mapLibre/Layer"
import { Feature, Geometry } from "geojson"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import useChatInfo from "@/hooks/useChatInfo"
import { selectActiveChat } from "@/reducers/chatsSliceSelectors"
import { format } from "date-fns"
import { convertToRoute2g } from "@/components/maps/SharedMap"
import { LngLatBounds } from "maplibre-gl"
import * as polyline from "@mapbox/polyline"
import PulsingDot from "@/components/maps/PulsingDot"
import { selectRouteJidFromChatJid } from "@/reducers/selectors"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"

const driverLocationLayer: LayerProps = {
  id: "location-point",
  type: "symbol",
  layout: {
    "icon-image": "pulsing-dot",
  },
}

function LateAlert() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const mapViewRef = useRef<MapViewHandle>(null)
  const lateAlert = useAppSelector(selectRouteAlert)
  const activeChat = useAppSelector(selectActiveChat)
  const routeJid = useAppSelector(selectRouteJidFromChatJid(activeChat.jid))
  const { driverParticipant } = useChatInfo(activeChat)

  // Track all timeouts for cleanup

  const [stops, setStops] = useState<Route2g | undefined>(undefined)
  const [routeInfo, setRouteInfo] = useState<RouteInfo | undefined>(undefined)
  const [driverLocationSource, setDriverLocationSource] = useState<
    Feature<Geometry, { [p: string]: any }> | undefined
  >(undefined)

  const handleClose = useCallback(() => {
    dispatch(hideRouteAlert())
  }, [dispatch])

  // Draw reference route on the map - using polling to check style loaded

  const refershDriverLocation = useCallback(() => {
    if (driverLocationSource) {
      setDriverLocationSource({ ...driverLocationSource })
    }
  }, [driverLocationSource])

  const fetchAlertData = useCallback(async () => {
    const { alertId } = lateAlert
    if (!routeJid || !alertId) return

    try {
      const result = await AdHocCommands.getRoutePolylines(
        window.XMPP!,
        routeJid.toString(),
        alertId,
      )

      const routeInfo = RoutePolylinesParser.parse(result)
        ?.route as RouteInfo & {
        driver_location: { lat: number; lng: number }
        stops: RouteStop[]
      }

      if (routeInfo && routeInfo.routes?.[0].legs && routeJid) {
        const fetchedStops = routeInfo.routes[0].legs.map((leg) => leg.stop)
        setStops(convertToRoute2g(fetchedStops, routeJid.toString()))

        setRouteInfo(routeInfo)
        if (routeInfo.driver_location) {
          setDriverLocationSource({
            type: "Feature",
            properties: {},
            geometry: {
              type: "Point",
              coordinates: [
                routeInfo.driver_location.lng,
                routeInfo.driver_location.lat,
              ],
            },
          })
        }
      }
    } catch (err) {
      console.error("Error fetching route for alert:", err)
    }
  }, [lateAlert])

  useEffect(() => {
    fetchAlertData()
  }, [])

  const showAllLayers = useCallback(() => {
    const mapInstance = mapViewRef.current?.getMapManager()?.getMapInstance()
    if (!mapInstance) return

    const bounds = new LngLatBounds()

    if (routeInfo) {
      const polylines = routeInfo.routes[0].legs.map((leg) =>
        polyline.decode(leg.polyline),
      )
      if (!polylines.length) return

      polylines
        .flatMap((polyline) => (Array.isArray(polyline) ? polyline : []))
        .forEach(([lat, lng]) => bounds.extend([lng, lat] as [number, number]))
    }

    if (!bounds.isEmpty()) {
      mapInstance.fitBounds(bounds, {
        padding: 40,
        maxZoom: 15,
        duration: 1000,
      })
    }

    if (driverLocationSource) {
      if (!mapInstance.hasImage("pulsing-dot")) {
        const pulsingDotImage = PulsingDot(mapInstance)
        mapInstance.addImage("pulsing-dot", pulsingDotImage, {
          pixelRatio: 2,
        })
        setTimeout(() => refershDriverLocation(), 1000)
      }
    }
  }, [routeInfo, driverLocationSource])

  useEffect(() => {
    requestAnimationFrame(() => showAllLayers())
  }, [routeInfo, showAllLayers])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose()
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [handleClose])
  const driverName = driverParticipant
    ? `${driverParticipant?.firstName} ${driverParticipant?.lastName}`
    : undefined
  const alertDate = lateAlert.updatedAt
    ? format(new Date(lateAlert.updatedAt), "HH:mm dd/MM/yyyy", {
        locale: getDateFnsLocale(),
      })
    : ""
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          handleClose()
        }
      }}
    >
      <DialogContent
        className="max-h-[90vh] sm:max-w-[50vw]"
        backgroundOpacity={50}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
        }}
      >
        <DialogHeader>
          <DialogTitle>{t("maps.lateAlert.title")}</DialogTitle>
          <DialogDescription>
            <div className="flex gap-1 pb-4 pt-2 text-muted-foreground">
              {driverName && (
                <>
                  <span>{driverName}</span>
                  <span>|</span>
                </>
              )}
              <span>{alertDate}</span>
            </div>
            <div className="relative mx-auto flex h-[75vh] w-full">
              {routeInfo && (
                <MapView
                  isAlertMap
                  mapViewRef={mapViewRef}
                  sharedStops={stops}
                  sharedRoute={routeInfo}
                  driverJid="shared"
                  isSharedMap
                >
                  {driverLocationSource && (
                    <Source type="geojson" data={driverLocationSource}>
                      <Layer {...driverLocationLayer} />
                    </Source>
                  )}
                </MapView>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  )
}

export default LateAlert
